import { useEffect, useRef, useState } from 'react'

declare const __APP_VERSION__: string | undefined

// vite-plugins/version-plugin.ts only injects __APP_VERSION__ via `define`; anything that
// imports this hook without going through that Vite config (a test run, most notably) never
// declares the identifier at all. `typeof` is the one operator that's safe against a
// ReferenceError on an undeclared identifier, so this falls back to 'dev' — the same value the
// plugin itself uses outside a real build — rather than crashing every consumer.
function currentVersion(): string {
  return typeof __APP_VERSION__ === 'undefined' ? 'dev' : __APP_VERSION__
}

const POLL_INTERVAL_MS = 5 * 60 * 1000
const BROADCAST_CHANNEL_NAME = 'rally-version-updates'
const DISMISSED_KEY = 'rally:dismissed-version'

interface VersionInfo {
  version: string
}

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(DISMISSED_KEY)
  } catch {
    return null
  }
}

function writeDismissed(version: string): void {
  try {
    sessionStorage.setItem(DISMISSED_KEY, version)
  } catch {
    // Storage unavailable (private browsing, quota) — "Later" just won't stick across a
    // reload in this tab, which is a harmless downgrade, not a broken feature.
  }
}

function reload(): void {
  window.location.reload()
}

export interface VersionCheck {
  updateAvailable: boolean
  dismiss: () => void
  reload: () => void
}

// RAL-137: polls the static version.json a build emits (vite-plugins/version-plugin.ts) and
// compares it to this bundle's own __APP_VERSION__. Deliberately no countdown or forced
// reload — Settings (RAL-125) holds real unsaved per-section dirty state that a blind timer
// could silently discard, so the prompt just waits for the user to act. Dismissing a version
// is remembered per-tab (sessionStorage) so "Later" doesn't nag again for the same deploy; a
// newer version detected afterward still prompts. A BroadcastChannel syncs the flag to a
// user's other open tabs without each one polling independently having to be the one to notice.
export function useVersionCheck(): VersionCheck {
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(readDismissed)
  const channelRef = useRef<BroadcastChannel | null>(null)

  useEffect(() => {
    if (currentVersion() === 'dev' || typeof BroadcastChannel === 'undefined') return
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
    channelRef.current = channel
    channel.onmessage = (event: MessageEvent<string>) => setLatestVersion(event.data)
    return () => channel.close()
  }, [])

  useEffect(() => {
    if (currentVersion() === 'dev') return

    let cancelled = false

    async function check() {
      if (!navigator.onLine) return
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok || cancelled) return
        const info = (await res.json()) as VersionInfo
        if (cancelled || info.version === currentVersion()) return
        setLatestVersion(info.version)
        channelRef.current?.postMessage(info.version)
      } catch {
        // Network blip — the next poll tries again.
      }
    }

    function onVisible() {
      if (document.visibilityState === 'visible') void check()
    }

    void check()
    const interval = setInterval(check, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  function dismiss(): void {
    if (!latestVersion) return
    writeDismissed(latestVersion)
    setDismissedVersion(latestVersion)
  }

  const updateAvailable = latestVersion !== null && latestVersion !== dismissedVersion

  return { updateAvailable, dismiss, reload }
}
